/* ---------------------------------------------------------------------------
   CERCO SEARCH RULES — SENTENCE MODE. Shared by the dropdown and the results
   page.
   ---------------------------------------------------------------------------
   Shopify's storefront search treats several words as OR: a product only has
   to match ONE of them to appear. That is why "16 ga" used to return 358
   products including "TEW 22 GA SOLID" — it matched "ga" and ignored "16".

   Shopify DOES understand quoted phrases. Everything the customer types is now
   sent as ONE quoted phrase, so the whole line has to appear in the product
   text, in that order, with nothing in between:

     typed  16 ga            ->  "16 ga"
     typed  16 ga shielded   ->  "16 ga shielded"
     typed  sjoow black      ->  "sjoow black"

   Three rules:
     1. The typed line is never broken up. Word order and spacing are part of
        the search, so a product only shows if it carries that exact wording.
     2. A number keeps its unit. A 22 GA product can no longer sneak in on the
        word "ga".
     3. No substitutions. "ga" means "ga". "awg" means "awg". Never mixed —
        quoting also stops Search & Discovery synonyms from widening the query.

   The trade-off, stated plainly: this only finds products whose description
   happens to be worded in the same order the customer typed. "sjoow black"
   drops from 9 products to 2, because the other 7 read "SJOOW 300V BLACK".
   Run the description cleanup first and that gap closes.

   To go back to word mode (every word required, order ignored), replace the
   single return line in buildQuery marked SENTENCE MODE with the word-mode
   block kept directly beneath it.
--------------------------------------------------------------------------- */
window.CercoSearch = (function () {
  "use strict";

  /* Words that belong to the number in front of them. Add to this list if the
     catalogue gains new unit words. */
  var UNIT_WORDS = [
    "ga", "awg", "gauge", "calibre", "jauge",
    "conductor", "conductors", "cond", "conducteur", "conducteurs",
    "pair", "pairs", "paire", "paires",
    "triad", "triads", "triade", "triades",
    "strand", "strands", "toron", "torons",
    "v", "volt", "volts", "kv", "c",
  ];

  var NUMBER = /^\d+(?:\/\d+)?$/;
  /* Spaces and every kind of dash, as escape sequences so the file survives
     copy/paste through the Shopify code editor. */
  var SPLIT = /[\s\-\u2010\u2011\u2012\u2013\u2014\u2015]+/;

  function fold(s) {
    return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  function isNumber(w) {
    return NUMBER.test(w);
  }

  function isUnit(w) {
    return UNIT_WORDS.indexOf(fold(w).toLowerCase()) !== -1;
  }

  /* Split the typed query into words. A leading dash is ignored, and hyphens in
     the middle separate, so "26 awg", "26-awg" and "-26 awg" all read alike. */
  function tokenize(raw) {
    var lead = /^[\s\-\u2010\u2011\u2012\u2013\u2014\u2015]+/;
    return (raw || "").trim().replace(lead, "").split(SPLIT).filter(Boolean);
  }

  /* Build the precise query. Returns null when the query is best left alone. */
  function buildQuery(raw) {
    /* Words are kept exactly as typed, accents and all — Shopify does its own
       accent folding. Folding is only used to RECOGNISE the unit words. */
    var words = tokenize(raw);
    if (!words.length) return null;

    /* One word on its own is already a sentence of one. Nothing to hold
       together, so it is left alone. */
    if (words.length === 1) return null;

    /* A lone number with no unit behind it — "16 copper", "3 black". Shopify is
       unreliable when it has to match a bare number strictly, so the rewrite is
       abandoned and the normal search runs. */
    for (var i = 0; i < words.length; i += 1) {
      if (isNumber(words[i]) && !(words[i + 1] && isUnit(words[i + 1]))) return null;
    }

    /* SENTENCE MODE — the whole typed line goes out as one quoted phrase, so
       the words must appear together, in order, with nothing in between. */
    return '"' + words.join(" ") + '"';

    /* WORD MODE — keep for reference. Every word required, order and any words
       in between ignored. Swap this block back in for the return line above to
       revert.

       var parts = [];
       var hasPhrase = false;
       var i = 0;
       while (i < words.length) {
         var word = words[i];
         var next = words[i + 1];
         if (isNumber(word) && next && isUnit(next)) {
           parts.push('"' + word + " " + next + '"');
           hasPhrase = true;
           i += 2;
           continue;
         }
         if (isNumber(word)) return null;
         parts.push(word);
         i += 1;
       }
       if (parts.length === 1) {
         return parts[0].charAt(0) === '"' ? parts[0] : null;
       }
       if (!hasPhrase) parts[0] = '"' + parts[0] + '"';
       return parts.join(" ");
    */
  }

  /* Turn a rewritten query back into what the customer typed, for display.
     "16 ga" shielded  ->  16 ga shielded */
  function prettify(q) {
    if (!q) return q;
    return q.split(" AND ").join(" ").replace(/"/g, "").trim();
  }

  function looksRewritten(q) {
    return !!q && (q.indexOf(" AND ") !== -1 || q.indexOf('"') !== -1);
  }

  return {
    UNIT_WORDS: UNIT_WORDS,
    fold: fold,
    isNumber: isNumber,
    isUnit: isUnit,
    tokenize: tokenize,
    buildQuery: buildQuery,
    prettify: prettify,
    looksRewritten: looksRewritten,
  };
})();

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;
    this.lastQuery = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  // ---------------------------------------------------------------------------
  // EXACT MATCHING
  // ---------------------------------------------------------------------------
  // The same sentence rule as the query rewrite above, applied to the dropdown:
  // the typed words must appear in the product text together and in order.
  // Two allowances, because the dropdown runs while the customer is still
  // typing:
  //   - the LAST word only has to match the start of a word, so "sjoow bla"
  //     still finds "SJOOW BLACK";
  //   - unless that last word is a number, which must match a whole word, so
  //     "16" does NOT match the part number 1651.

  // Strip accents so "blinde" with an accent and without are the same word.
  foldAccents(raw) {
    return window.CercoSearch.fold(raw);
  }

  getQueryTokens(raw) {
    return window.CercoSearch.tokenize(this.foldAccents((raw || "").toLowerCase()));
  }

  // The typed words, lower-cased and stripped of accents, as one sequence the
  // product text has to contain in that order.
  getRequirements(raw) {
    return this.getQueryTokens(raw);
  }

  // Does the product's own words contain the typed sequence, in order and side
  // by side? The last typed word may still be half-finished, so it matches on
  // its start — unless it is a number, which has to be a whole word.
  containsSequence(words, seq) {
    const C = window.CercoSearch;
    const last = seq.length - 1;
    const lastIsNumber = C.isNumber(seq[last]);

    for (let start = 0; start + seq.length <= words.length; start += 1) {
      let ok = true;
      for (let k = 0; k < seq.length; k += 1) {
        const word = words[start + k];
        const want = seq[k];
        const matched =
          k === last && !lastIsNumber ? word.indexOf(want) === 0 : word === want;
        if (!matched) {
          ok = false;
          break;
        }
      }
      if (ok) return true;
    }

    return false;
  }

  // Remove any product suggestion that does NOT contain the typed sentence.
  // If nothing does, show no products at all (no partial results).
  filterExactMatches() {
    const seq = this.getRequirements(this.lastQuery);
    if (!seq.length) return;

    const items = this.predictiveSearchResults.querySelectorAll(
      ".predictive-search-list-item-product",
    );
    let kept = 0;

    items.forEach((li) => {
      const raw = this.foldAccents(
        (li.getAttribute("data-search-text") || li.textContent || "").toLowerCase(),
      );
      const words = raw.split(/[^a-z0-9/]+/).filter(Boolean);

      if (this.containsSequence(words, seq)) {
        kept += 1;
      } else {
        li.remove();
      }
    });

    // Nothing matched -> drop the whole products section so the dropdown never
    // shows "close but wrong" partial matches.
    if (kept === 0) {
      const heading = this.predictiveSearchResults.querySelector("#predictive-search-products");
      const section = heading ? heading.closest(".predictive-search-results-group-section") : null;
      if (section) section.remove();
    }
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    // Empty box, or a highlighted suggestion the customer is about to open:
    // let the existing behaviour win and do nothing else.
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Otherwise send the PRECISE query instead of the loose one, so the results
    // page returns exactly what was typed.
    const typed = this.getQuery();
    const precise = window.CercoSearch.buildQuery(typed);
    if (!precise || precise === typed) return;

    event.preventDefault();

    const action = event.target.getAttribute("action") || "/search";
    const params = new URLSearchParams();
    params.set("q", precise);
    params.set("type", "product");
    window.location.href = action + "?" + params.toString();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    // Remember exactly what was typed so we can apply the rules after fetch.
    this.lastQuery = searchTerm;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    // Search fields: keep "body" (description) and "tag" so Shopify returns every
    // candidate; we then apply the rules client-side (filterExactMatches).
    fetch(
      `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,body,tag`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Enforce the matching rules before displaying.
    this.filterExactMatches();

    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    const top = this.querySelector(".predictive-search").getBoundingClientRect().top;
    this.predictiveSearchResults.style.maxHeight = `calc(100dvh - ${top}px - 30px)`;

    if (!this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsOpen);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsClose);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);

/* ---------------------------------------------------------------------------
   RESULTS PAGE — show the customer their own words.

   The search itself is sent precisely: typing "SJOOW BLACK" sends
   SJOOW AND BLACK, which returns the 9 SJOOW cables that are black instead of
   the 96 products that merely mention one of the two words. But the customer
   should never SEE that syntax, so this puts the heading and the search box
   back to exactly what was typed.

   There is deliberately NO widening when a search finds nothing. If no product
   matches every word the customer typed, the theme's own "no results" message
   is shown, naming the words they typed. The search never quietly swaps in a
   looser query and returns products they did not ask for.

   The hidden q input belonging to FacetsFilterForm is deliberately left on the
   precise query — otherwise clicking a filter would widen the search again.
--------------------------------------------------------------------------- */
(function () {
  "use strict";

  var C = window.CercoSearch;
  if (!C) return;

  function run() {
    var params = new URLSearchParams(window.location.search);
    var q = params.get("q");

    /* Only touch a query this theme rewrote. A plain search is left alone. */
    if (!C.looksRewritten(q)) return;

    var pretty = C.prettify(q);

    /* Nothing found stays nothing found. The theme's own no-results message is
       left in place; the only thing done to it is printing the typed words
       instead of the query syntax, which the code below handles. */

    /* The browser tab. */
    if (document.title.indexOf(q) !== -1) {
      document.title = document.title.split(q).join(pretty);
    }

    /* Wherever the raw query was printed - page heading, "N results for ...",
       the no-results message - print the typed words instead. */
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null, false);
    var hits = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue && node.nodeValue.indexOf(q) !== -1) hits.push(node);
    }
    hits.forEach(function (n) {
      n.nodeValue = n.nodeValue.split(q).join(pretty);
    });

    /* Visible search boxes only, never the hidden FacetsFilterForm input. */
    var boxes = document.querySelectorAll('input[type="search"][name="q"]');
    Array.prototype.forEach.call(boxes, function (box) {
      if (box.value === q) box.value = pretty;
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();